import {useForm} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Group, Modal, ModalProps, Stack, Switch} from "@mantine/core";
import {useCallback} from "react";
import {transportMethod, volumeInventoryVolumeType} from "@ots-frontend/ots-data";
import {InputInfoTooltip} from "../../tooltip/InfoTooltip/InfoTooltip";

interface VolumeInventoryChartFormProps {
  defaultValues?: Partial<VolumeInventoryChartFormValues>;
  onSubmit?: (values: VolumeInventoryChartFormValues) => void;
}

export interface VolumeInventoryChartFormValues {
  includeUsableVolume?: boolean
  includeUnusableVolume?: boolean
  includeSecondaryUsableVolume?: boolean
  includeSecondaryUnusableVolume?: boolean
  includeHauledVolume?: boolean
  includeTrucking?: boolean
  includePipeline?: boolean
  splitVolumes?: boolean
  splitHaulMethods?: boolean
}

export const VolumeInventoryChartForm = (props: VolumeInventoryChartFormProps) => {
  const {
    defaultValues,
    onSubmit,
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useForm({
    initialValues: {
      includeUsableVolume: defaultValues?.includeUsableVolume,
      includeUnusableVolume: defaultValues?.includeUnusableVolume,
      includeSecondaryUsableVolume: defaultValues?.includeSecondaryUsableVolume,
      includeSecondaryUnusableVolume: defaultValues?.includeSecondaryUnusableVolume,
      includeHauledVolume: defaultValues?.includeHauledVolume,
      includeTrucking: defaultValues?.includeTrucking,
      includePipeline: defaultValues?.includePipeline,
      splitVolumes: defaultValues?.splitVolumes,
      splitHaulMethods: defaultValues?.splitHaulMethods,
    },
    validate: {}
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        includeUsableVolume: values.includeUsableVolume,
        includeUnusableVolume: values.includeUnusableVolume,
        includeSecondaryUsableVolume: values.includeSecondaryUsableVolume,
        includeSecondaryUnusableVolume: values.includeSecondaryUnusableVolume,
        includeHauledVolume: values.includeHauledVolume,
        includeTrucking: values.includeTrucking,
        includePipeline: values.includePipeline,
        splitVolumes: values.splitVolumes,
        splitHaulMethods: values.splitHaulMethods,
      });
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <Stack spacing="md">
        <Switch
          label={
            <Group spacing={"xs"}>
              {t(volumeInventoryVolumeType.usable.displayValue.id, volumeInventoryVolumeType.usable.displayValue.defaultMessage)}
              <InputInfoTooltip
                label={"Show only usable volumes for the primary cargo in the container."}/>
            </Group>
          }
          {...form.getInputProps("includeUsableVolume", {type: 'checkbox'})} />
        <Switch
          label={
            <Group spacing={"xs"}>
              {t(volumeInventoryVolumeType.unusable.displayValue.id, volumeInventoryVolumeType.unusable.displayValue.defaultMessage)}
              <InputInfoTooltip
                label={"Show only unusable volumes for the primary cargo in the container."}/>
            </Group>
          }
          {...form.getInputProps("includeUnusableVolume", {type: 'checkbox'})} />
        <Switch
          label={
            <Group spacing={"xs"}>
              {t(volumeInventoryVolumeType.secondaryUsable.displayValue.id, volumeInventoryVolumeType.secondaryUsable.displayValue.defaultMessage)}
              <InputInfoTooltip
                label={"Show only usable volumes for all non-primary cargo types in the container."}/>
            </Group>
          }
          {...form.getInputProps("includeSecondaryUsableVolume", {type: 'checkbox'})} />
        <Switch
          label={
            <Group spacing={"xs"}>
              {t(volumeInventoryVolumeType.secondaryUnusable.displayValue.id, volumeInventoryVolumeType.secondaryUnusable.displayValue.defaultMessage)}
              <InputInfoTooltip
                label={"Show only unusable volumes for all non-primary cargo types in the container."}/>
            </Group>
          }
          {...form.getInputProps("includeSecondaryUnusableVolume", {type: 'checkbox'})} />
        <Divider/>
        <Switch
          label={
            <Group spacing={"xs"}>
              {"Hauled Volume"}
            </Group>
          }
          {...form.getInputProps("includeHauledVolume", {type: 'checkbox'})} />
        <Divider/>
        <Switch
          label={
            <Group spacing={"xs"}>
              {t(transportMethod.trucking.displayValue.id, transportMethod.trucking.displayValue.defaultMessage)}
            </Group>
          }
          {...form.getInputProps("includeTrucking", {type: 'checkbox'})} />
        <Switch
          label={
            <Group spacing={"xs"}>
              {t(transportMethod.pipeline.displayValue.id, transportMethod.pipeline.displayValue.defaultMessage)}
            </Group>
          }
          {...form.getInputProps("includePipeline", {type: 'checkbox'})} />
        <Divider/>
        <Switch
          label={
            <Group spacing={"xs"}>
              {t("common.insight.volumeinventory.chart.form.field.splithaulmethods", "Split Haul Methods")}
              <InputInfoTooltip
                label={"Whether or not to split the data set by haul methods (i.e. Trucking, Pipeline)."}/>
            </Group>
          }
          {...form.getInputProps("splitHaulMethods", {type: 'checkbox'})} />
        <Switch
          label={
            <Group spacing={"xs"}>
              {t("common.insight.volumeinventory.chart.form.field.splitvolumes", "Split Volumes")}
              <InputInfoTooltip label={"Whether or not to split the data set by volume types."}/>
            </Group>
          }
          {...form.getInputProps("splitVolumes", {type: 'checkbox'})} />
      </Stack>
      <Button type="submit" fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface VolumeInventoryChartFormModalProps extends VolumeInventoryChartFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const VolumeInventoryChartFormModal = (props: VolumeInventoryChartFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <VolumeInventoryChartForm defaultValues={defaultValues} onSubmit={onSubmit}/>
    </Modal>
  );
};
